import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { mobile, tablet, InsetSection } from '~styles/global'
import { graphql, Link } from 'gatsby'
import DataViewer from '~utils/DataViewer'
import { Logo } from '~components/Svg'
import Section from '~components/Section'
import RichText from '~components/RichText'
import Image from '~components/Image'
import SanityLink from '~components/SanityLink'
import { AnimatePresence, motion } from 'framer-motion'
import { useSiteState } from '~context/siteContext'
import Seo from '~components/Seo'

const About = ({ data }) => {
	const page = data?.sanityAbout
	const [image, setImage] = useState(null)
	const [siteState, setSiteState] = useSiteState()

	useEffect(() => {
		setSiteState(prevState => ({
			...prevState,
			pageTitle: page.title,
		}))
	}, [])

	return (
		<>
			<Seo 
				title={page.title}
				metaTitle={page.seo?.metaTitle}
				description={page.seo?.metaDescription}
				image={page.seo?.socialImage}
			/>
			<Wrap>
				{/* <LogoSection>
					<LargeLogo />
				</LogoSection> */}
				<MainSection>
					<Col>
						<LargeLogo />
						<IntroText><RichText content={page?.introText}/></IntroText>
						<Text><RichText content={page?.mainText} /></Text>
						<AboutImage image={page?.image}/>
					</Col>
					<Col>
						<LineList>
							<LineListTitle>Contact</LineListTitle>
							<ContactText>
								<RichText content={page?.contact}/>
							</ContactText>
						</LineList>
						<LineList>
							<LineListTitle>{page?.clients?.title}</LineListTitle>
							<ClientList>{page?.clients?.items.map((client, i) => (
								<React.Fragment key={client._key}>
									<Client 
										link={client.link}
										pink={client?.link?.linkType === 'external' || client.link.document}
									>
										{client.title}
									</Client>
									{i + 1 !== page?.clients?.items.length && ', '}
								</React.Fragment>			
							))}</ClientList>
						</LineList>
						<LineList>
							<LineListTitle>{page.services?.title}</LineListTitle>
							{page.services?.items.map(service => (
								<LineListItem key={service._key} link={service.link} pink={service?.link?.linkType === 'external' || service.link.document}>{service.title}</LineListItem>
							))}				
						</LineList>
						{page.featureProjects.length > 0 &&
							<LineList>
								<LineListTitle>{page.featureProjectsTitle}</LineListTitle>
								{page.featureProjects?.map((project, i) => (
									<LineListItem 
										key={project._key} 
										link={{
											linkType: 'internal',
											document: project
										}}
										hover
									>
										<hoverImageTrigger 
											onMouseEnter={() => setImage(i)}
											onMouseLeave={() => setImage(undefined)}
										>
											{project.title}
										</hoverImageTrigger>
									</LineListItem>
								))}				
							</LineList>
						}
						<LineList>	
							<LineListTitle>{page.awards?.title}</LineListTitle>
							{page.awards?.items?.map(award => (
								<LineListItem key={award._key} link={award.link} pink={award?.link?.linkType === 'external' || award.link.document}>{award.title}</LineListItem>
							))}				
						</LineList>
						<LineList>
							<LineListTitle>{page.associations?.title}</LineListTitle>
							{page.associations?.items?.map(association => (
								<LineListItem key={association._key} link={association.link} pink={association?.link?.linkType === 'external' || association.link.document}>{association.title}</LineListItem>
							))}				
						</LineList>
						<LineList>
							<LineListTitle>{page.press?.title}</LineListTitle>
							{page.press?.items?.map(pressItem => (
								<LineListItem key={pressItem._key} link={pressItem.link} pink={pressItem?.link?.linkType === 'external' || pressItem.link.document}>{pressItem.title}</LineListItem>
							))}				
						</LineList>
					</Col>
				</MainSection>
				<AnimatePresence>
					<ImageSection
						key={image}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={{ duration: 0.2}}
					>
						{page.featureProjects?.map(project => (
							<HoverImage 
								key={project._key} 
								image={page.featureProjects[image]?.headerMedia?.image}
							/>
						))}		
					</ImageSection>
				</AnimatePresence>
				<DataViewer data={page} name="page"/>
			</Wrap>
		</>
	)
}

const Wrap = styled.div`
	background-color: var(--white);
	padding-top: 115px;
	${mobile}{
		padding-top: 110px;
	}
`
const LogoSection = styled(InsetSection)`
	margin-top: 100px;
	margin-bottom: 110px;
	${mobile}{
		margin-top: 40px;
		margin-bottom: 52px;
	}
`
const LargeLogo = styled(Logo)`
	/* max-width: 1364px; */
	width: 100%;
	margin-bottom: 90px;
	${mobile}{
		margin-bottom: 40px;
	}
	/* grid-column: span 12; */
`
const MainSection = styled(InsetSection)`
	position: relative;
	z-index: 2;
	margin-top: 160px;
	margin-bottom: 212px;
	>div{
		grid-template-columns: 1fr 32%;
    grid-column-gap: 15%;
	}
	${tablet}{
		margin-top: 40px;
		margin-bottom: 100px;
	}
`
const Col = styled.div`
	grid-column: span 1;
	${mobile}{
		grid-column: span 2;
	}
`
const IntroText = styled.div`
	margin-bottom: 1em;
`
const Text = styled.div`
	margin-bottom: 75px;
	${mobile}{
		margin-bottom: 52px;
	}
`
const ContactText = styled.div`
	margin-bottom: 46px;
	${mobile}{
		margin-bottom: 34px;
	}
`
const ClientList = styled.div`
	margin-bottom: 46px;
	${mobile}{
		margin-bottom: 34px;
	}
`
const Client = styled(SanityLink)`
	transition: color 0.2s;
	:hover{
		color: ${props => (props?.hover || props.pink) ? 'var(--pink)' : 'var(--black)'};
	}
`
const AboutImage = styled(Image)`
	margin-bottom: 115px;
	${mobile}{
		margin-bottom: 60px;
	}
`
const LineListTitle = styled.div`
	padding-bottom: 4px;
	border-bottom: 1px solid var(--black);
	margin-bottom: 0.25em;
`
const LineList = styled.div`
	margin-bottom: 46px;
	${mobile}{
		margin-bottom: 34px;
	}
`
const LineListItem = styled(SanityLink)`
	display: block;
	padding-bottom: 6px;
	/* padding-top: 3px; */
	/* border-bottom: 1px solid var(--black); */
	transition: color 0.2s;
	:hover{
		color: ${props => (props?.hover || props.pink) ? 'var(--pink)' : 'var(--black)'};
	} 
`
const ImageSection = styled(motion.div)`
	position: fixed;
	left: 0;
	bottom: 0;
	right: 0;
	top: 0;
	z-index: 1;
	pointer-events: none;
	margin: 0 60px;
`
const HoverImage = styled(Image)`
	max-width: 878px;
	width: 100%;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`
About.propTypes = {
	data: PropTypes.object
}

export const query = graphql`
	  query AboutQuery($id: String) {
    	sanityAbout(id: {eq: $id}) {
				title
				slug {
					current
				}
				seo{
					...seo
				}
				introText: _rawIntroText(resolveReferences: {maxDepth: 5})
				mainText: _rawMainText(resolveReferences: {maxDepth: 5})
				contact: _rawContact(resolveReferences: {maxDepth: 5})
				clients {
					title
					items {
						_key
						title
						link{
							...link
						}
					}
				}
				image {
					...imageWithAlt
				}
				services {
					title
					items {
						_key
						title
						link{
							...link
						}
					}
				}
				featureProjectsTitle
				featureProjects {
					slug {
						current
					}
					_type
					title
					headerMedia {
						image {
							...imageWithAlt
						}
					}
				}
				awards {
					title
					items {
						_key
						title
						link{
							...link
						}
					}
				}
				associations {
					title
					items {
						_key
						title
						link{
							...link
						}
					}
				}
				press {
					title
					items {
						_key
						title
						link{
							...link
						}
					}
				}
			}
		}
`

export default About